import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Stack,
  Text,
  Tooltip,
  Icon,
  FormLayout,
  ButtonGroup,
  TextField,
  Banner,
  Divider,
  Toast
} from '@shopify/polaris';
import {
  QuestionMarkMinor,
  SaveMinor,
  RefreshMinor
} from '@shopify/polaris-icons';
import { UserSettingsService } from '../../services/userSettingsService';

export default function ProductTools({
  selectedProductIds,
  products,
  onProductUpdate,
  isProductUpdating,
  editHistory,
  user,
  localEdits,
  setLocalEdits
}) {
  const [prefix, setPrefix] = useState('');
  const [suffix, setSuffix] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [altTextSuffix, setAltTextSuffix] = useState(' - product image');
  const [showHelp, setShowHelp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [productTools, setProductTools] = useState({
    prefix: '',
    suffix: '',
    vendorName: '',
    altTextSuffix: ' - product image'
  });

  // Load saved product tools settings on component mount
  useEffect(() => {
    console.log('ProductTools component mounted, user:', user);
    if (user?.uid) {
      loadProductSettings();
    }
  }, [user]);

  const loadProductSettings = async () => {
    console.log('loadProductSettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to load settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      const savedSettings = await UserSettingsService.getUserSettings(user.uid, 'product');
      if (savedSettings) {
        setProductTools(savedSettings);
        setPrefix(savedSettings.prefix || '');
        setSuffix(savedSettings.suffix || '');
        setVendorName(savedSettings.vendorName || '');
        setAltTextSuffix(savedSettings.altTextSuffix || ' - product image');
        showToastMessage('Product settings loaded successfully', false);
      }
    } catch (error) {
      console.error('Error loading product settings:', error);
      showToastMessage('Failed to load product settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProductSettings = async () => {
    console.log('saveProductSettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to save settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      const settings = {
        prefix,
        suffix,
        vendorName,
        altTextSuffix
      };
      
      setProductTools(settings);
      await UserSettingsService.saveUserSettings(user.uid, 'product', settings);
      showToastMessage('Product settings saved successfully', false);
    } catch (error) {
      console.error('Error saving product settings:', error);
      showToastMessage('Failed to save product settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const showToastMessage = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setShowToast(true);
  };

  const handleEmptyHandles = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    selectedProducts.forEach(product => {
      // Add update to clear the handle field
      updates.push({
        productId: product.id,
        field: 'handle',
        value: ''
      });
    });

    console.log('Generated handle updates:', updates);

    if (updates.length > 0) {
      onProductUpdate(updates);
    }
  };

  const handleAddPrefixSuffix = () => {
    if (selectedProductIds.size === 0 || (!prefix && !suffix)) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    selectedProducts.forEach(product => {
      // Get the current handle from UI updates if available, otherwise use the original handle
      let currentHandle = '';
      
      // Check if the product has any UI updates for the handle field
      if (editHistory && editHistory[product.id] && editHistory[product.id].handle) {
        currentHandle = editHistory[product.id].handle;
      } else if (product._updates && product._updates.handle) {
        currentHandle = product._updates.handle;
      } else if (product.updates && product.updates.handle) {
        currentHandle = product.updates.handle;
      } else {
        // Fall back to the original product handle if no UI updates exist
        currentHandle = product.handle || '';
      }
      
      // Log the handle source for debugging
      console.log(`Using handle for prefix/suffix: ${currentHandle} (from ${editHistory && editHistory[product.id] && editHistory[product.id].handle ? 'editHistory' : (product._updates?.handle ? '_updates' : (product.updates?.handle ? 'updates' : 'original'))})`);
      
      // Create the new handle with prefix and suffix
      const newHandle = `${prefix}${currentHandle}${suffix}`;
      
      // Add update to modify the handle field
      updates.push({
        productId: product.id,
        field: 'handle',
        value: newHandle
      });
    });

    console.log('Generated handle updates with prefix/suffix:', updates);

    if (updates.length > 0) {
      onProductUpdate(updates);
    }
  };

  const handleGenerateHandlesFromTitle = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    selectedProducts.forEach(product => {
      // Get the current title from the UI field
      // This is the fix - we need to check if there are any UI updates for the title
      // The title could be in localEdits or other UI state that's passed to this component
      let title = '';
      
      // Check if the product has any UI updates for the title field
      // This assumes that products might have a _updates or updates property with UI changes
      if (editHistory && editHistory[product.id] && editHistory[product.id].title) {
        title = editHistory[product.id].title;
      } else if (product._updates && product._updates.title) {
        title = product._updates.title;
      } else if (product.updates && product.updates.title) {
        title = product.updates.title;
      } else {
        // Fall back to the original product title if no UI updates exist
        title = product.title || '';
      }
      
      // Log the title source for debugging
      console.log(`Using title for handle generation: ${title} (from ${editHistory && editHistory[product.id] && editHistory[product.id].title ? 'editHistory' : (product._updates?.title ? '_updates' : (product.updates?.title ? 'updates' : 'original'))})`); 
      
      // Create a handle from the title: lowercase, replace spaces with hyphens, remove special chars
      const newHandle = title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-')     // Replace spaces with hyphens
        .replace(/-+/g, '-');     // Replace multiple hyphens with single hyphen
      
      // Add update to modify the handle field
      updates.push({
        productId: product.id,
        field: 'handle',
        value: newHandle
      });
    });

    console.log('Generated handles from titles:', updates);

    if (updates.length > 0) {
      onProductUpdate(updates);
    }
  };

  // Helper function to slugify text for SKUs
  const slugifyForSku = (text) => {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')     // Replace spaces with hyphens
      .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Trim hyphens from start and end
  };

  // Helper function to get variant option values
  const getVariantOptionValues = (variant) => {
    const optionValues = [];
    
    // Try to get option values from different possible sources
    if (variant.optionValues && Array.isArray(variant.optionValues)) {
      // If optionValues array exists, use it directly
      return variant.optionValues
        .filter(val => val && val.trim() !== '')
        .map(val => slugifyForSku(val))
        .join('-');
    }
    
    // Otherwise try to get from option1, option2, option3 properties
    if (variant.option1 && variant.option1.trim() !== '') {
      optionValues.push(slugifyForSku(variant.option1));
    }
    
    if (variant.option2 && variant.option2.trim() !== '') {
      optionValues.push(slugifyForSku(variant.option2));
    }
    
    if (variant.option3 && variant.option3.trim() !== '') {
      optionValues.push(slugifyForSku(variant.option3));
    }
    
    // If still no option values, try selectedOptions
    if (optionValues.length === 0 && variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
      variant.selectedOptions.forEach(option => {
        if (option.value && option.value.trim() !== '') {
          optionValues.push(slugifyForSku(option.value));
        }
      });
    }
    
    return optionValues.join('-');
  };

  // Get first N words from a string
  const getFirstNWords = (text, n = 2) => {
    if (!text) return '';
    
    // Split by spaces and take first n words
    const words = text.trim().split(/\s+/);
    return words.slice(0, n).join(' ');
  };

  // Generate SKUs for all variants of selected products
  const handleGenerateSkus = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];
    
    // Object to store variant SKUs for UI update
    const variantSkuUpdates = {};
    
    selectedProducts.forEach(product => {
      // Get the current title from the UI field or original product
      let title = '';
      
      if (editHistory && editHistory[product.id] && editHistory[product.id].title) {
        title = editHistory[product.id].title;
      } else if (product._updates && product._updates.title) {
        title = product._updates.title;
      } else if (product.updates && product.updates.title) {
        title = product.updates.title;
      } else {
        title = product.title || '';
      }
      
      // Get first 2 words of the title and slugify
      const shortTitle = getFirstNWords(title, 2);
      const slugifiedTitle = slugifyForSku(shortTitle);
      
      // Initialize variant SKU updates for this product
      if (!variantSkuUpdates[product.id]) {
        variantSkuUpdates[product.id] = {};
      }
      
      // Process each variant
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach(variant => {
          // Get variant option values
          const optionValuesString = getVariantOptionValues(variant);
          
          // Build the SKU with optional prefix and suffix
          let newSku = '';
          
          // Add prefix if exists
          if (prefix) {
            newSku += prefix;
          }
          
          // Add slugified title if exists
          if (slugifiedTitle) {
            if (newSku && !newSku.endsWith('-')) newSku += '-';
            newSku += slugifiedTitle;
          }
          
          // Add option values if exist
          if (optionValuesString) {
            if (newSku && !newSku.endsWith('-')) newSku += '-';
            newSku += optionValuesString;
          }
          
          // Add suffix if exists
          if (suffix) {
            if (newSku && !newSku.endsWith('-')) newSku += '-';
            newSku += suffix;
          }
          
          // Ensure SKU doesn't exceed 255 characters
          if (newSku.length > 255) {
            newSku = newSku.substring(0, 255);
          }
          
          // Store the new SKU for UI update
          variantSkuUpdates[product.id][variant.id] = { sku: newSku };
          
          // Add update to modify the SKU field for this variant
          updates.push({
            productId: product.id,
            variantId: variant.id,
            field: 'sku',
            value: newSku
          });
        });
      }
    });

    console.log('Generated SKUs for variants:', updates);

    if (updates.length > 0) {
      // Update the local edits directly to ensure UI consistency
      setLocalEdits(prevEdits => {
        const updatedEdits = { ...prevEdits };
        
        updates.forEach(update => {
          const { productId, variantId, field, value } = update;
          
          // Initialize product in localEdits if needed
          if (!updatedEdits[productId]) {
            updatedEdits[productId] = { variant_updates: {}, _timestamp: Date.now() };
          }
          if (!updatedEdits[productId].variant_updates) {
            updatedEdits[productId].variant_updates = {};
          }
          
          // Initialize variant in localEdits if needed
          if (!updatedEdits[productId].variant_updates[variantId]) {
            updatedEdits[productId].variant_updates[variantId] = {};
          }
          
          // Update the SKU field
          updatedEdits[productId].variant_updates[variantId][field] = value;
          
          // Update timestamp to force re-render
          updatedEdits[productId]._timestamp = Date.now();
          
          console.log('Updated variant SKU in localEdits:', {
            productId,
            variantId,
            field,
            value,
            variant: updatedEdits[productId].variant_updates[variantId]
          });
        });
        
        return updatedEdits;
      });
      
      // Try to update the UI fields directly as well
      try {
        // Import the utility function
        const { updateVariantSkuFieldsInUI } = require('../../utils/productUtils');
        
        // Update each product's variant SKU fields
        Object.entries(variantSkuUpdates).forEach(([productId, variants]) => {
          updateVariantSkuFieldsInUI(productId, variants);
        });
      } catch (error) {
        console.error('Error updating SKU fields in UI:', error);
      }
      
      // Send the updates to the parent component
      onProductUpdate(updates);
      
      // Show success message
      setShowToast(true);
      setToastMessage('SKUs generated and applied successfully');
      setToastError(false);
    }
  };

  const handleSetVendor = () => {
    if (selectedProductIds.size === 0 || !vendorName.trim()) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    selectedProducts.forEach(product => {
      // Add update to set the vendor field
      updates.push({
        productId: product.id,
        field: 'vendor',
        value: vendorName.trim()
      });
    });

    console.log('Generated vendor updates:', updates);

    if (updates.length > 0) {
      onProductUpdate(updates);
    }
  };

  const handleGenerateImageAltText = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];

    selectedProducts.forEach(product => {
      // Generate alt text from product title
      const altText = product.title + (altTextSuffix || '');
      
      // Add update to set the image alt text field
      updates.push({
        productId: product.id,
        field: 'image_alt_text',
        value: altText.trim()
      });
    });

    console.log('Generated image alt text updates:', updates);

    if (updates.length > 0) {
      onProductUpdate(updates);
    }
  };

  return (
    <Card>
      {showToast && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setShowToast(false)}
        />
      )}
      
      <Card.Section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text variant="headingLg">Product Tools</Text>
            <Tooltip content="Tools to help you manage product handles and other product attributes">
              <Icon source={QuestionMarkMinor} color="base" onClick={() => setShowHelp(!showHelp)} />
            </Tooltip>
          </div>
          
          <ButtonGroup>
            <Button 
              icon={RefreshMinor} 
              onClick={loadProductSettings} 
              disabled={isLoading || !user}
              tooltip="Load saved settings"
            >
              Load Settings
            </Button>
            <Button 
              icon={SaveMinor} 
              onClick={saveProductSettings} 
              disabled={isLoading || !user}
              primary
              tooltip="Save current settings"
            >
              Save Settings
            </Button>
          </ButtonGroup>
        </div>

        {showHelp && (
          <Banner title="About Product Tools" status="info" onDismiss={() => setShowHelp(false)}>
            <p>These tools help you make bulk changes to product attributes.</p>
            <p><strong>Handle Tools:</strong> Modify product handles which are used in the URL of your product pages.</p>
            <p><strong>Vendor Tool:</strong> Set the vendor name for all selected products.</p>
          </Banner>
        )}

        <Text variant="headingMd" as="h3">Handle Tools</Text>
        <FormLayout>
          <ButtonGroup fullWidth>
            <Button
              onClick={handleEmptyHandles}
              disabled={selectedProductIds.size === 0 || isProductUpdating}
              loading={isProductUpdating}
            >
              Empty Handles
            </Button>
            <Button
              onClick={handleGenerateHandlesFromTitle}
              disabled={selectedProductIds.size === 0 || isProductUpdating}
            >
              Generate From Title
            </Button>
          </ButtonGroup>

          <FormLayout.Group>
            <TextField
              label="Prefix"
              value={prefix}
              onChange={setPrefix}
              placeholder="e.g., new-"
              autoComplete="off"
            />
            <TextField
              label="Suffix"
              value={suffix}
              onChange={setSuffix}
              placeholder="e.g., -2023"
              autoComplete="off"
            />
          </FormLayout.Group>
          
          <Button
            onClick={handleAddPrefixSuffix}
            disabled={selectedProductIds.size === 0 || isProductUpdating || (!prefix && !suffix)}
          >
            Apply Prefix/Suffix
          </Button>
        </FormLayout>
      </Card.Section>

      <Divider />

      <Card.Section>
        <Text variant="headingMd" as="h3">SKU Generator</Text>
        <FormLayout>
          <Banner status="info">
            <p>Generate SKUs for all variants of selected products using the format:</p>
            <p><code>[prefix]-[slugified product title]-[variant options]-[suffix]</code></p>
            <p>Each segment is optional. Variant options are included in order (e.g., Color/Size → black-xl).</p>
          </Banner>
          
          <FormLayout.Group>
            <TextField
              label="Prefix"
              value={prefix}
              onChange={setPrefix}
              placeholder="e.g., WF"
              autoComplete="off"
              helpText="Added at the beginning of the SKU"
            />
            <TextField
              label="Suffix"
              value={suffix}
              onChange={setSuffix}
              placeholder="e.g., 2025"
              autoComplete="off"
              helpText="Added at the end of the SKU"
            />
          </FormLayout.Group>
          
          <Button
            onClick={handleGenerateSkus}
            disabled={selectedProductIds.size === 0 || isProductUpdating}
            primary
          >
            Generate & Apply SKUs
          </Button>
        </FormLayout>
      </Card.Section>

      <Divider />

      <Card.Section>
        <Text variant="headingMd" as="h3">Vendor Tool</Text>
        <FormLayout>
          <TextField
            label="Vendor Name"
            value={vendorName}
            onChange={setVendorName}
            placeholder="Enter vendor name"
            autoComplete="off"
            helpText="Set the vendor name for all selected products"
          />
          
          <Button
            onClick={handleSetVendor}
            disabled={selectedProductIds.size === 0 || isProductUpdating || !vendorName.trim()}
            primary
          >
            Set Vendor Name
          </Button>
        </FormLayout>
      </Card.Section>

      <Divider />

      <Card.Section>
        <Text variant="headingMd" as="h3">Image Alt Text Tool</Text>
        <Text variant="bodyMd" as="p" color="subdued">Generates image alt text using the product title with an optional suffix.</Text>
        <FormLayout>
          <TextField
            label="Suffix (optional)"
            value={altTextSuffix}
            onChange={setAltTextSuffix}
            placeholder="e.g., product image"
            autoComplete="off"
            helpText="Add a suffix to the product title in the alt text. Default: ' - product image'"
          />
          
          <Button
            onClick={handleGenerateImageAltText}
            disabled={selectedProductIds.size === 0 || isProductUpdating}
            primary
          >
            Generate Alt Text from Title
          </Button>
        </FormLayout>
      </Card.Section>
    </Card>
  );
}
